import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "api/axiosInstance";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import { LanguageContext } from "../../../../context/LanguageContext";
import {
  Card,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Divider,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

function RecoveryTokenManager() {
  // tokens：只在「剛重新產生」時短暫持有明碼（顯示一次）；remaining：DB 內剩餘可用組數
  const [tokens, setTokens] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showLowTokenDialog, setShowLowTokenDialog] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  
  useEffect(() => {
    loadLanguageFile("recoverytoken", "2fa/RecoveryTokenManager/RecoveryTokenManager_lang");
  }, [language, loadLanguageFile]);
  
  const LANGUAGE = componentLanguageData.recoverytoken || { Main: {}, Error: {}, Alert: {} };

  // 🛡️ M5：備援碼改存雜湊 → 後端不再回傳明碼清單，只回剩餘組數。
  //   明碼僅在「重新產生」的當下回傳一次（下面 regenerateTokens），離開頁面就再也看不到。
  const loadTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/users/recovery-tokens");
      const count = typeof res.data.remaining === "number" ? res.data.remaining : 0;
      setRemaining(count);
      if (count > 0 && count < 5) {
        setShowLowTokenDialog(true);
      }
    } catch (err) {
      setError(LANGUAGE.Error.Content1);
    } finally {
      setLoading(false);
    }
  }, [LANGUAGE.Error.Content1]);

  const regenerateTokens = async () => {
    setRefreshing(true);
    try {
      const res = await axiosInstance.post("/users/recovery-tokens/regenerate");
      // 明碼只有這一刻拿得到 → 顯示給使用者抄下（同時已寄到信箱）
      setTokens(res.data.tokens || []);
      await loadTokens();
      setSuccess(LANGUAGE.Alert.Content1 || "Tokens regenerated successfully");
    } catch (err) {
      setError(LANGUAGE.Error.Content2);
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setSuccess(LANGUAGE.Main.Content3 || "Copied to clipboard");
    } catch (err) {
      setError("Failed to copy");
    }
  };

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={10}>
          <Card sx={{ overflow: "visible" }}>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDBox>
                <MDTypography variant="h5" color="white">
                  {LANGUAGE.Main.Content1}
                </MDTypography>
                <MDTypography variant="button" color="white" opacity={0.8}>
                  {remaining} {LANGUAGE.Main.Content8}
                </MDTypography>
              </MDBox>
              <Icon fontSize="medium" sx={{ color: "white" }}>security</Icon>
            </MDBox>

            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <MDTypography variant="h6" color="text">
                  {LANGUAGE.Main.Content9}
                </MDTypography>
                <Box>
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => navigate("/2fa/manage-ga")}
                    sx={{ mr: 1 }}
                  >
                    {LANGUAGE.Main.Content10}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="warning"
                    size="small"
                    onClick={regenerateTokens}
                    disabled={refreshing}
                    startIcon={<Icon>refresh</Icon>}
                  >
                    {refreshing ? LANGUAGE.Alert.Content1 : LANGUAGE.Alert.Content2}
                  </MDButton>
                </Box>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {loading ? (
                <MDBox display="flex" justifyContent="center" py={5}>
                  <CircularProgress color="info" />
                </MDBox>
              ) : error ? (
                <MDTypography color="error" align="center">{error}</MDTypography>
              ) : tokens.length === 0 ? (
                // 🛡️ M5：平時看不到明碼，只顯示剩餘組數 / 或提示尚未產生
                remaining > 0 ? (
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    py={6}
                    sx={{
                      border: `2px dashed ${theme.palette.divider}`,
                      borderRadius: 2,
                      backgroundColor: theme.palette.grey[50],
                    }}
                  >
                    <Icon fontSize="large" color="info" sx={{ mb: 2, fontSize: "3rem !important" }}>
                      verified_user
                    </Icon>
                    <MDTypography variant="h6" color="text" gutterBottom>
                      {remaining} {LANGUAGE.Main.Content8}
                    </MDTypography>
                    <MDTypography variant="button" color="text" opacity={0.8} align="center" sx={{ maxWidth: 460 }}>
                      {LANGUAGE.Main.Content15 ||
                        "為了安全，備援碼只在產生當下顯示一次（並寄到你的信箱）。若已遺失，請按上方「重新產生」取得新的一組。"}
                    </MDTypography>
                  </MDBox>
                ) : (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  py={6}
                  sx={{
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[50],
                  }}
                >
                  <Icon fontSize="large" color="disabled" sx={{ mb: 2, fontSize: "3rem !important" }}>
                    lock_open
                  </Icon>
                  <MDTypography variant="h6" color="text" gutterBottom>
                    {LANGUAGE.Main.Content2}
                  </MDTypography>
                </MDBox>
                )
              ) : (
                <>
                {/* 🛡️ M5：這些明碼只會出現這一次 → 明確提醒使用者立即保存 */}
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {LANGUAGE.Main.Content16 ||
                    "請立即複製並妥善保存以下備援碼——離開此頁後將無法再次查看（同一份已寄到你的信箱）。"}
                </Alert>
                <Grid container spacing={2}>
                  {tokens.map((token, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={idx}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          transition: "all 0.2s",
                          "&:hover": {
                            borderColor: theme.palette.info.main,
                            boxShadow: theme.shadows[2],
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <MDTypography 
                          variant="button" 
                          fontWeight="medium" 
                          sx={{ 
                            fontFamily: "monospace", 
                            fontSize: "1rem",
                            wordBreak: "break-all",
                            overflow: "hidden",
                            flex: 1,
                            minWidth: 0
                          }}
                        >
                          {token}
                        </MDTypography>

                        <Tooltip title={LANGUAGE.Main.Content3 || "Copy"}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(token)}
                            color="info"
                            sx={{ flexShrink: 0 }}
                          >
                            <Icon fontSize="small">content_copy</Icon>
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                </>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={showLowTokenDialog} onClose={() => setShowLowTokenDialog(false)}>
        <DialogTitle>{LANGUAGE.Main.Content4}</DialogTitle>
        <DialogContent>
          <MDTypography>{LANGUAGE.Main.Content5}</MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLowTokenDialog(false)} color="secondary">
            {LANGUAGE.Main.Content6}
          </Button>
          <Button
            onClick={() => {
              setShowLowTokenDialog(false);
              regenerateTokens();
            }}
            autoFocus
            color="warning"
            variant="contained"
            sx={{ color: "white" }}
          >
            {LANGUAGE.Main.Content7}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSuccess("")}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!error && !loading} 
        autoHideDuration={3000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError("")}>
          {error}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default RecoveryTokenManager;
